import React, { useState, useEffect } from "react";
import { GetCustomersList } from "api/customers";
import { getAllCustomerGroups } from "api/customerGroup";
import { GetRouteList } from "api/routes";
import { GetAgentList } from "api/agent";
import {
  Box,
  useTheme,
  IconButton,
  Link,
  Backdrop,
  CircularProgress,
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  Avatar,
  InputBase,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import { Search } from "@mui/icons-material";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import ModeEditOutlineIcon from "@mui/icons-material/ModeEditOutline";
import { useNavigate } from "react-router-dom";
import { isMobile } from "react-device-detect";
import CustomerEditDialog from "components/CustomerEditDialog";
import FlexBetween from "components/FlexBetween";
import StyledDataGrid from "components/bento/StyledDataGrid";
import BentoCard from "components/bento/BentoCard";
import { BG_TINT, BG_WHITE, BORDER, RADIUS_INPUT, RADIUS_TILE, CARD_SHADOW, PURPLE_LIGHT, TEXT_SUBTLE, TEXT_SECONDARY } from "components/bento/bentoTokens";

const CustomerList = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);

  // values to be sent to the backend
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState({});
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // values to set in DataGrid
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [customerGroups, setCustomerGroups] = useState([]);

  // Filters: route, customer group, agent, COD, sub customer, status
  const [routeList, setRouteList] = useState([]);
  const [agentList, setAgentList] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [selectedCOD, setSelectedCOD] = useState("");
  const [selectedSubCustomer, setSelectedSubCustomer] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const clearFilters = () => {
    setSelectedRoute("");
    setSelectedGroupId("");
    setSelectedAgentId("");
    setSelectedCOD("");
    setSelectedSubCustomer("");
    setSelectedStatus("");
  };

  // ... (pagination, sorting, search state and related useEffects)

  // Fetch customer list from API
  const fetchCustomers = (page, pageSize, sort, search) => {
    setIsLoading(true);
    // Status "All" (default) mirrors the previous always-on behaviour (validflag=true, all=true).
    const validflag = selectedStatus === "inactive" ? false : true;
    const all = selectedStatus === "" ? true : false;
    const filters = {
      route: selectedRoute || undefined,
      groupId: selectedGroupId || undefined,
      agentId: selectedAgentId || undefined,
      isCOD: selectedCOD || undefined,
      isSubCustomer: selectedSubCustomer || undefined,
    };
    GetCustomersList(search, page, pageSize, sort, validflag, all, filters)
      .then((res) => {
        if (res && res.success) {
          if (isMobile) {
            setRows((prevRows) => {
              const newRows = res.data.filter(
                (newItem) =>
                  !prevRows.some(
                    (existingItem) => existingItem._id === newItem._id
                  )
              );
              return [...prevRows, ...newRows];
            });
          } else {
            setRows(res.data);
          }
          setTotalCount(res.total);
        } else {
          console.error(res?.message);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Debounce search
  let timer;
  const setSearchWithDelaying = (val) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => setSearch(val), 1000);
  };

  // Load customer groups once on mount
  useEffect(() => {
    getAllCustomerGroups().then((result) => {
      if (result.success) {
        setCustomerGroups(result.data);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load routes and agents once on mount for the filter dropdowns
  useEffect(() => {
    GetRouteList().then((res) => {
      if (res && res.success) setRouteList(res.data);
    });
    GetAgentList().then((res) => {
      if (res && res.success) setAgentList(res.data);
    });
  }, []);

  const filterDeps = [
    selectedRoute,
    selectedGroupId,
    selectedAgentId,
    selectedCOD,
    selectedSubCustomer,
    selectedStatus,
  ];

  // Refresh when page/pageSize/sort/search/filters change
  useEffect(() => {
    fetchCustomers(page, pageSize, sort, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sort, search, ...filterDeps]);

  // Infinite scroll for mobile — listens on the layout's main scroll container
  useEffect(() => {
    if (!isMobile) return;
    const container = document.getElementById("main-scroll-container") || window;
    const handleScroll = () => {
      const el = /** @type {Element} */ (container);
      const scrollTop    = container === window ? window.scrollY     : el.scrollTop;
      const clientHeight = container === window ? window.innerHeight : el.clientHeight;
      const scrollHeight = container === window ? document.documentElement.scrollHeight : el.scrollHeight;
      if (scrollTop + clientHeight >= scrollHeight - 200 && !isLoading && rows.length < totalCount) {
        setPage((prevPage) => prevPage + 1);
      }
    };
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [isLoading, rows.length, totalCount]);

  // Reset rows when search or filters change (mobile infinite scroll)
  useEffect(() => {
    setRows([]);
    setPage(0);
    fetchCustomers(0, pageSize, sort, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, ...filterDeps]);

  const buttonEditHandler = (event, cellValues) => {
    setEditingCustomer(cellValues.row);
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditingCustomer(null);
  };

  const columns = [
    // ... (all your DataGrid columns)
    {
      field: "_id",
      headerName: "ID",
      flex: 1,
      hide: true,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 0.5,
      renderCell: (cellValues) => {
        return (
          <Link
            component="button"
            color="secondary"
            underline="hover"
            onClick={() => navigate(`/customer/${cellValues.row._id}`)}
          >
            {cellValues.row.name}
          </Link>
        );
      },
    },
    {
      field: "whatsapp",
      headerName: "WhatsApp",
      flex: 0.7,
    },
    {
      field: "groupId",
      headerName: "Group",
      flex: 0.6,
      hide: isMobile,
      renderCell: (cellValues) => {
        const group = customerGroups.find(g => g.id === cellValues.row.groupId);
        return group ? (
          <div>{group.name}</div>
        ) : (
          <span style={{ color: TEXT_SUBTLE }}>No Group</span>
        );
      },
    },
    {
      field: "default_route",
      headerName: "Default Route",
      flex: 0.5,
      hide: false,
    },
    {
      field: "isCOD",
      headerName: "COD",
      headerAlign: "center",
      align: "center",
      flex: 0.5,
      hide: isMobile,
      renderCell: (cellValues) => {
        return cellValues.row.isCOD ? (
          <CheckBoxIcon color="secondary" />
        ) : (
          <DoNotDisturbIcon color="primary" />
        );
      },
    },
    {
      field: "isDeliveryRequired",
      headerName: "ຈັດສົ່ງຮອດເຮືອນ",
      headerAlign: "center",
      align: "center",
      flex: 0.5,
      hide: isMobile,
      renderCell: (cellValues) => {
        return cellValues.row.isDeliveryRequired ? (
          <CheckBoxIcon color="secondary" />
        ) : (
          <DoNotDisturbIcon color="primary" />
        );
      },
    },
    { field: "deliveryAddresses", headerName: "Location", hide: true, flex: 1 },

    {
      field: "address",
      headerName: "Address",
      flex: 1,
      valueGetter: (params) => {
        let address;
        if (isMobile) {
          address = params.row.province.pr_name;
        } else {
          address = params.row.province.pr_name;
          address += params.row.district.dt_name
            ? ", " + params.row.district.dt_name
            : "";
        }

        return address;
      },
      hide: true,
    },
    {
      field: "isForwardRequired",
      headerName: "ຝາກຂົນສົ່ງຕໍ່",
      headerAlign: "center",
      align: "center",
      flex: 0.5,
      hide: isMobile,
      renderCell: (cellValues) => {
        return cellValues.row.isForwardRequired ? (
          <CheckBoxIcon color="secondary" />
        ) : (
          <DoNotDisturbIcon color="primary" />
        );
      },
    },
    { field: "forwardAddresses", headerName: "Addresses", hide: true, flex: 1 },

    {
      field: "remark",
      headerName: "Remark",
      flex: 1,
      hide: isMobile,
    },
    {
      field: "isSelfOrder",
      headerName: "ສັ່ງເຄື່ອງເອງ",
      headerAlign: "center",
      align: "center",
      flex: 0.5,
      hide: isMobile,
      renderCell: (cellValues) => {
        return cellValues.row.isSelfOrder ? (
          <CheckBoxIcon color="secondary" />
        ) : (
          <DoNotDisturbIcon color="primary" />
        );
      },
    },
    {
      field: "isSubCustomer",
      headerName: "ລູກຄ້າຂອງຕົວແທນ",
      headerAlign: "center",
      align: "center",
      flex: 0.5,
      hide: isMobile,
      renderCell: (cellValues) => {
        return cellValues.row.isSubCustomer ? (
          <CheckBoxIcon color="secondary" />
        ) : (
          <DoNotDisturbIcon color="primary" />
        );
      },
    },
    {
      field: "agentId",
      headerName: "ລະຫັດຕົວແທນ",
      flex: 0.5,
      hide: isMobile,
    },
    {
      field: "validflag",
      headerName: "Validflag",
      flex: 0.5,
      hide: isMobile,
      renderCell: (cellValues) => {
        return cellValues.row.validflag ? (
          <ToggleOnIcon color="secondary" />
        ) : (
          <ToggleOffIcon />
        );
      },
    },
    {
      field: "edit",
      headerName: "Edit",
      flex: 0.2,
      renderCell: (cellValues) => (
        <IconButton
          variant="outline"
          onClick={(event) => buttonEditHandler(event, cellValues)}
          sx={{ "&:hover": { color: theme.palette.warning.main } }}
        >
          <ModeEditOutlineIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <BentoCard sx={{ mb: "16px" }} bodySx={{ p: "20px 22px" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", xl: "repeat(6, 1fr)" },
            gap: "16px",
            alignItems: "start",
          }}
        >
          <FormControl fullWidth>
            <InputLabel>Route</InputLabel>
            <Select
              label="Route"
              value={selectedRoute}
              onChange={(e) => setSelectedRoute(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {routeList.map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  {id} ({name})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Group</InputLabel>
            <Select
              label="Group"
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {customerGroups.map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Agent</InputLabel>
            <Select
              label="Agent"
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {agentList.map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  {id} ({name})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>COD</InputLabel>
            <Select
              label="COD"
              value={selectedCOD}
              onChange={(e) => setSelectedCOD(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Sub Customer</InputLabel>
            <Select
              label="Sub Customer"
              value={selectedSubCustomer}
              onChange={(e) => setSelectedSubCustomer(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ mt: "16px", display: "flex", justifyContent: "flex-end" }}>
          <Button variant="outlined" color="secondary" onClick={clearFilters}>
            Clear filters
          </Button>
        </Box>
      </BentoCard>

      {!isMobile ? (
        <Box height="80vh">
        <StyledDataGrid
          loading={isLoading}
          getRowId={(row) => row._id}
          rows={rows || []}
          columns={columns}
          rowCount={totalCount || 0}
          rowsPerPageOptions={[20, 50, 100]}
          pagination
          page={page}
          pageSize={pageSize}
          paginationMode="server"
          sortingMode="server"
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          onSortModelChange={(newSortModel) => setSort(...newSortModel)}
          components={{ Toolbar: DataGridCustomToolbar }}
          componentsProps={{
            toolbar: { searchInput, setSearchInput, setSearch },
          }}
          containerSx={{ height: "100%" }}
          autoHeight={false}
        />
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FlexBetween
                sx={{
                  backgroundColor: BG_TINT,
                  border: `1px solid ${BORDER}`,
                  borderRadius: RADIUS_INPUT,
                  gap: "3rem",
                  p: "0.1rem 1.5rem",
                  mb: 2,
                }}
              >
                <InputBase
                  placeholder="Search customers..."
                  onChange={(e) => setSearchWithDelaying(e.target.value)}
                />
                <IconButton>
                  <Search />
                </IconButton>
              </FlexBetween>
            </Grid>
            {rows &&
              rows.map((customer, index) => {
                // Find customer group name
                const customerGroup = customerGroups.find(group => group.id === customer.groupId);
                const groupName = customerGroup ? customerGroup.name : 'No Group';
                
                // Generate unique color for each group
                const generateGroupColor = (groupId, groupName) => {
                  if (!groupId && groupName === 'No Group') {
                    return {
                      backgroundColor: BG_TINT,
                      color: TEXT_SECONDARY,
                      borderColor: BORDER
                    };
                  }
                  
                  // Create a hash from groupId to ensure consistent colors
                  const hash = groupId ? groupId.split('').reduce((a, b) => {
                    a = ((a << 5) - a) + b.charCodeAt(0);
                    return a & a;
                  }, 0) : 0;
                  
                  // Define a set of attractive colors
                  const colors = [
                    { bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' }, // Blue
                    { bg: '#f3e5f5', color: '#7b1fa2', border: '#ce93d8' }, // Purple
                    { bg: '#e8f5e8', color: '#2e7d32', border: '#81c784' }, // Green
                    { bg: '#fff3e0', color: '#ef6c00', border: '#ffb74d' }, // Orange
                    { bg: '#fce4ec', color: '#c2185b', border: '#f48fb1' }, // Pink
                    { bg: '#e0f2f1', color: '#00695c', border: '#80cbc4' }, // Teal
                    { bg: '#f1f8e9', color: '#558b2f', border: '#aed581' }, // Light Green
                    { bg: '#e8eaf6', color: '#3f51b5', border: '#9fa8da' }, // Indigo
                    { bg: '#fff8e1', color: '#ff8f00', border: '#ffcc02' }, // Amber
                    { bg: '#ffebee', color: '#d32f2f', border: '#ef5350' }  // Red
                  ];
                  
                  const colorIndex = Math.abs(hash) % colors.length;
                  const selectedColor = colors[colorIndex];
                  
                  return {
                    backgroundColor: selectedColor.bg,
                    color: selectedColor.color,
                    borderColor: selectedColor.border
                  };
                };
                
                const groupColors = generateGroupColor(customer.groupId, groupName);
                
                return (
                  <Grid item key={customer._id} xs={12} sm={6} md={4}>
                    <Card sx={{ background: BG_WHITE, border: `1px solid ${BORDER}`, borderRadius: RADIUS_TILE, boxShadow: CARD_SHADOW }}>
                      <CardHeader
                        onClick={()=>navigate(`/customer/${customer._id}`)}
                        avatar={
                          <Avatar
                            sx={{
                              bgcolor: customer.validflag
                                ? PURPLE_LIGHT
                                : TEXT_SUBTLE,
                            }}
                          >
                            {customer.name.charAt(0).toUpperCase()}
                          </Avatar>
                        }
                        title={customer.name}
                        subheader={`WhatsApp: ${customer.whatsapp}`}
                        action={
                          <Box sx={{ textAlign: "right" }}>
                            <Typography variant="caption" color="text.secondary">
                              {customer.validflag ? "Active" : "Inactive"}
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                              <Chip
                                label={groupName}
                                size="small"
                                sx={{
                                  backgroundColor: groupColors.backgroundColor,
                                  color: groupColors.color,
                                  borderColor: groupColors.borderColor,
                                  fontWeight: 'bold',
                                  '&:hover': {
                                    backgroundColor: groupColors.backgroundColor,
                                    filter: 'brightness(0.9)',
                                  }
                                }}
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                        }
                      />
                      <CardContent sx={{ pt: 0 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Route:</strong> {customer.default_route}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Location:</strong> {customer.province?.pr_name}, {customer.district?.dt_name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                          {customer.isCOD && (
                            <Chip label="COD" size="small" color="secondary" />
                          )}
                          {customer.isDeliveryRequired && (
                            <Chip label="Home Delivery" size="small" color="info" />
                          )}
                          {customer.isForwardRequired && (
                            <Chip label="Forward Shipping" size="small" color="warning" />
                          )}
                          {customer.isSelfOrder && (
                            <Chip label="Self Order" size="small" color="success" />
                          )}
                        </Box>
                        {customer.isSubCustomer && customer.agentId && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            <strong>Agent:</strong> {customer.agentId}
                          </Typography>
                        )}
                        {customer.remark && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            <strong>Remark:</strong> {customer.remark}
                          </Typography>
                        )}
                      </CardContent>
                      <CardActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          #{index + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => buttonEditHandler(e, { row: customer })}                          
                        >
                          <ModeEditOutlineIcon />
                        </IconButton>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
          </Grid>
        </Box>
      )}

      <CustomerEditDialog
        open={editDialogOpen}
        customerData={editingCustomer}
        onClose={handleEditDialogClose}
        rows={rows}
        setRows={setRows}
      />
    </Box>
  );
};

export default CustomerList;
